import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../api/axiosInstance';

// Fetch invoices from API
export const fetchInvoices = createAsyncThunk(
  'billing/fetchInvoices',
  async ({ page = 1, page_size = 5 }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/billing/invoices/`, {
        params: { page, page_size },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to fetch invoices'
      );
    }
  }
);

// Create a new invoice
export const createInvoice = createAsyncThunk(
  'billing/createInvoice',
  async (invoiceData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        '/billing/invoices/',
        invoiceData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to create invoice'
      );
    }
  }
);

// Update invoice status
export const updateInvoiceStatus = createAsyncThunk(
  'billing/updateInvoiceStatus',
  async ({ invoiceId, status }, { rejectWithValue }) => {
    try {
      await axiosInstance.patch(
        `/billing/invoices/${invoiceId}/update_status/`,
        { status }
      );
      return { invoiceId, status };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to update invoice status'
      );
    }
  }
);

// Download invoice PDF
export const downloadInvoicePDF = createAsyncThunk(
  'billing/downloadPDF',
  async (invoiceId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/billing/invoices/${invoiceId}/generate_pdf/`,
        { responseType: 'blob' }
      );
      return { invoiceId, blob: response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to download invoice'
      );
    }
  }
);

const billingSlice = createSlice({
  name: 'billing',
  initialState: {
    invoices: [],
    loading: false,
    error: null,
    count: 0,
    next: null,
    previous: null,
    downloading: false,
    downloadError: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchInvoices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInvoices.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        // Handle both paginated and non-paginated responses
        const payload = action.payload;
        if (payload?.results) {
          // Paginated response
          state.invoices = Array.isArray(payload.results) ? payload.results : [];
          state.count = payload.count || 0;
          state.next = payload.next || null;
          state.previous = payload.previous || null;
        } else if (Array.isArray(payload)) {
          // Non-paginated array response
          state.invoices = payload;
          state.count = payload.length;
          state.next = null;
          state.previous = null;
        } else {
          state.invoices = [];
          state.count = 0;
          state.next = null;
          state.previous = null;
        }
      })
      .addCase(fetchInvoices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.detail || action.payload?.error || action.payload || 'Error fetching invoices';
      })
      .addCase(createInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createInvoice.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        // Add new invoice to the top of the list
        if (action.payload) {
          state.invoices = [action.payload, ...state.invoices];
          state.count = (state.count || 0) + 1;
        }
      })
      .addCase(createInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.detail || action.payload?.error || action.payload || 'Failed to create invoice. Please try again.';
      })
      .addCase(updateInvoiceStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateInvoiceStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const index = state.invoices.findIndex(
          (inv) => inv.id === action.payload.invoiceId
        );
        if (index !== -1) {
          state.invoices[index].status = action.payload.status;
        }
      })
      .addCase(updateInvoiceStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.detail || action.payload?.error || action.payload || 'Failed to update invoice status';
      })
      .addCase(downloadInvoicePDF.pending, (state) => {
        state.downloading = true;
        state.downloadError = null;
      })
      .addCase(downloadInvoicePDF.fulfilled, (state, action) => {
        state.downloading = false;
        // Create download link and trigger download
        const { invoiceId, blob } = action.payload;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `invoice_${invoiceId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .addCase(downloadInvoicePDF.rejected, (state, action) => {
        state.downloading = false;
        state.downloadError = action.payload;
      });
  },
});

export default billingSlice.reducer;
